<script setup lang="ts">
import { NewsEntry } from '@beeboard/common';
import Popover from 'primevue/popover';
import { useNewsStore } from '@/store/newsStore';
import { useUserRulesStore } from '@/store/userRulesStore';
import { useAlerts } from '@/utils/helpers/alerts';
import { resolveTranslation } from '@/utils/helpers/i18n-content';
import { formatDate } from '@/utils/helpers/various';
import type { ComponentOptions } from 'vue';

const router = useRouter();
const newsStore = useNewsStore();
const userRulesStore = useUserRulesStore();
const message = useAlerts();
const popover = ref();
const selectedEntry = ref<NewsEntry | null>(null);
const expandedEntry = ref<NewsEntry | null>(null);
const showExpandedModal = ref(false);

const unreadCount = computed(() => newsStore.unreadCount);

/** The parts of PrimeVue's Popover (4.3) that the resize listener below uses. */
interface PopoverInternals {
  visible: boolean;
  resizeListener: (() => void) | null;
  alignOverlay(): void;
}

/**
 * PrimeVue's Popover closes on every window resize. Full screen on a video in
 * the panel resizes the window, so the panel closed, removed the video and
 * thereby ended full screen (WI #5788). This one stays open and moves along
 * with the rocket button.
 */
const NewsPopover = defineComponent({
  name: 'NewsPopover',
  // PrimeVue types its components as constructors; they are options objects.
  extends: Popover as unknown as ComponentOptions,
  methods: {
    bindResizeListener(this: PopoverInternals) {
      if (!this.resizeListener) {
        this.resizeListener = () => {
          if (this.visible) {
            this.alignOverlay();
          }
        };
        window.addEventListener('resize', this.resizeListener);
      }
    },
  },
});

onMounted(async () => {
  await loadNews();
});

function toggle(event: MouseEvent) {
  popover.value.toggle(event);
}

async function loadNews() {
  try {
    await newsStore.loadNewsEntries();
  } catch (err) {
    message.error(err);
  }
}

async function selectEntry(entry: NewsEntry) {
  selectedEntry.value = entry;
  if (!entry.is_viewed) {
    try {
      await newsStore.recordView(entry.id);
    } catch (err) {
      message.error(err);
    }
  }
}

function backToList() {
  selectedEntry.value = null;
}

function expandEntry() {
  if (!selectedEntry.value) {
    return;
  }
  expandedEntry.value = selectedEntry.value;
  popover.value.hide();
  showExpandedModal.value = true;
}

function closeExpandedModal() {
  showExpandedModal.value = false;
  expandedEntry.value = null;
}

function onContentClick(event: MouseEvent) {
  const anchor = (event.target as HTMLElement).closest('a');
  if (anchor?.href) {
    event.preventDefault();
    window.open(anchor.href, '_blank', 'noopener');
  }
}

async function navigateToManage() {
  popover.value.hide();
  await router.push({ name: 'CompanyInAppContent' });
}
</script>

<template>
  <UiButton variant="flat" class="notification-button" @click="toggle">
    <UiIcon name="Rocket" class="icon-size-sm" />
    <span v-if="unreadCount > 0" class="unread-dot" />
  </UiButton>

  <NewsPopover
    ref="popover"
    :pt="{ content: { style: { padding: '0' } } }"
    @show="loadNews"
  >
    <div class="news-panel">
      <div class="news-header">
        <div class="news-header-left">
          <UiButton
            v-if="selectedEntry"
            variant="flat"
            size="small"
            @click="backToList"
          >
            <UiIcon name="ArrowLeft" class="icon-size-xs" />
          </UiButton>
          <h3>{{ $t('news.title') }}</h3>
        </div>
        <UiButton
          v-if="userRulesStore.canManageAnyContent()"
          variant="flat"
          size="small"
          @click="navigateToManage"
        >
          <UiIcon name="Settings" class="icon-size-xs" />
          {{ $t('news.manage') }}
        </UiButton>
      </div>

      <div v-if="!selectedEntry" class="news-list">
        <div v-if="newsStore.newsEntries.length === 0" class="news-empty">
          <p>{{ $t('news.empty') }}</p>
        </div>
        <div
          v-for="entry in newsStore.newsEntries"
          :key="entry.id"
          class="news-item"
          :class="{ 'news-unread': !entry.is_viewed }"
          @click="selectEntry(entry)"
        >
          <div class="news-item-title">
            {{ resolveTranslation(entry.title) }}
          </div>
          <div class="news-item-meta">
            <span v-if="entry.published_at">{{
              formatDate(entry.published_at)
            }}</span>
          </div>
        </div>
      </div>

      <div v-else class="news-detail">
        <div class="news-detail-header">
          <h4>{{ resolveTranslation(selectedEntry.title) }}</h4>
          <UiButton variant="flat" size="small" @click="expandEntry">
            <UiIcon name="Maximize2" class="icon-size-xs" />
          </UiButton>
        </div>
        <div class="news-detail-meta">
          <span v-if="selectedEntry.published_at">{{
            formatDate(selectedEntry.published_at)
          }}</span>
        </div>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="news-detail-content"
          @click="onContentClick"
          v-html="resolveTranslation(selectedEntry.content)"
        />
        <!-- eslint-enable vue/no-v-html -->
      </div>
    </div>
  </NewsPopover>

  <UiOverlay v-model="showExpandedModal">
    <UiModal
      v-if="expandedEntry"
      :title="resolveTranslation(expandedEntry.title)"
      is-wide
      @close="closeExpandedModal"
    >
      <div class="expanded-meta">
        <span v-if="expandedEntry.published_at">{{
          formatDate(expandedEntry.published_at)
        }}</span>
      </div>
      <!-- eslint-disable vue/no-v-html -->
      <div
        class="expanded-content"
        @click="onContentClick"
        v-html="resolveTranslation(expandedEntry.content)"
      />
      <!-- eslint-enable vue/no-v-html -->
    </UiModal>
  </UiOverlay>
</template>

<style scoped lang="scss">
.unread-dot {
  position: absolute;
  bottom: 6px;
  right: 6px;
  z-index: 1;
  width: 8px;
  height: 8px;
  background-color: var(--color-success);
  border-radius: var(--border-radius-circle);
  pointer-events: none;
}

.news-panel {
  width: 420px;
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

.news-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-10) var(--space-12);
  border-bottom: 1px solid var(--color-gray-200);

  h3 {
    margin: 0;
    font-size: var(--font-size-14);
    font-weight: var(--font-weight-bold);
  }
}

.news-header-left {
  display: flex;
  align-items: center;
  gap: var(--space-4);
}

.news-list {
  overflow-y: auto;
  max-height: 400px;
}

.news-empty {
  padding: var(--space-24);
  text-align: center;
  color: var(--color-gray-500);
}

.news-item {
  padding: var(--space-10) var(--space-12);
  cursor: pointer;
  border-bottom: 1px solid var(--color-gray-100);

  &:hover {
    background-color: var(--color-gray-50);
  }
}

.news-unread {
  background-color: rgba(var(--color-primary-50--rgb), 0.6);
}

.news-item-title {
  font-size: var(--font-size-13);
  font-weight: var(--font-weight-medium);
}

.news-item-meta {
  font-size: var(--font-size-11);
  color: var(--color-gray-500);
  margin-top: var(--space-4);
  display: flex;
  gap: var(--space-8);
}

.news-detail {
  padding: var(--space-12);
  overflow-y: auto;
  max-height: 400px;
}

.news-detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-8);

  h4 {
    margin: 0 0 var(--space-4);
    font-size: var(--font-size-15);
  }
}

.news-detail-meta {
  font-size: var(--font-size-11);
  color: var(--color-gray-500);
  margin-bottom: var(--space-12);
  display: flex;
  gap: var(--space-8);
}

.news-detail-content {
  font-size: var(--font-size-13);
  line-height: var(--line-height-ratio-150);
  overflow-x: hidden;
  word-break: break-word;

  :deep(a) {
    color: var(--color-primary, #2563eb);
    text-decoration: underline;
    cursor: pointer;
  }

  :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: var(--border-radius-4);
  }

  :deep(.iframe-wrapper) {
    max-width: 100%;
  }

  :deep(.iframe-wrapper iframe) {
    width: 100%;
    aspect-ratio: 16 / 9;
    border: none;
    border-radius: var(--border-radius-4);
  }
}

.expanded-meta {
  font-size: var(--font-size-12);
  color: var(--color-gray-500);
  margin-bottom: var(--space-16);
}

.expanded-content {
  font-size: var(--font-size-14);
  line-height: var(--line-height-ratio-160);
  overflow-x: hidden;
  overflow-y: auto;
  max-height: calc(100vh - 200px);
  word-break: break-word;

  :deep(a) {
    color: var(--color-primary, #2563eb);
    text-decoration: underline;
    cursor: pointer;
  }

  :deep(img) {
    max-width: 100%;
    height: auto;
    border-radius: var(--border-radius-4);
  }

  :deep(.iframe-wrapper) {
    max-width: 100%;
  }

  :deep(.iframe-wrapper iframe) {
    width: 100%;
    aspect-ratio: 16 / 9;
    border: none;
    border-radius: var(--border-radius-4);
  }

  :deep(*) {
    max-width: 100%;
  }
}
</style>
